// "Any 3 small tins" deal page (snippets/page-any-3-teas.liquid).
// The basket is the only record (Michael 2026-10-02): a card's Add puts that
// tea's small tin straight in it, and once it's in, the card shows the
// product page's quantity stepper, pointed at that basket line - so - and +
// go through cardo like the cart drawer's. Cards and the bar always show
// what is in the basket, however it got there. The deal price itself will
// come from our discount app, in the cart.
//
//   [data-deal-add="<variant id>"][data-deal-handle]  a card's Add button
//   [data-deal-stepper="<variant id>"]  its stepper (snippets/quantity-input)
//   [data-deal-bar]  the bar: data-deal-tin-ids (which tins count, | joined),
//                    data-deal-size (3), prices in cents: data-deal-set-price
//                    (all 3), data-deal-extra-price (each tin after), data-deal-was (usual)
//
// ?tea=<handle> (retargeting ads: "Peach Blush + any 2 more") puts that tea
// in the basket on arrival - once per visit, only if it isn't in already,
// never while the page is only being prerendered. The page stays at the top
// and the bar names the tea instead of "1 of 3 chosen" (Michael 2026-10-09).
// The handle is the product handle, which is also the Meta catalogue's
// retailer_product_group_id (checked against all 21 teas 2026-10-09).
// Sold-out teas have no card (page-any-3-teas.liquid), so a link to one adds
// nothing and the bar says so until the first tin goes in
// (data-deal-sold-out on the bar lists them as "handle::Title|...").
//
// Trio ads (2026-10-09, trios in snippets/deal-trios, on the bar as
// data-deal-trios="key::Name::h,h,h[::bar line]|..."): ?trio=<key>, e.g. ?trio=earl-grey,
// puts that trio's three small tins in the basket on arrival and opens the
// basket (Michael 2026-10-10: no trio cards on the page). ?tea='s rules: once
// per visit, never while prerendering, tins already in the basket skipped,
// sold-out tins skipped and named in the bar. While the basket holds exactly
// the trio, the bar names it; a swap is still any 3.
//
// Trio pages (/pages/any-3-teas-<key>, page-trio.liquid, Michael 2026-10-12):
// nothing on arrival. [data-deal-trio-add] buttons (on the page and in the
// bar) carry the trio's in-stock small tin ids (data-deal-trio-ids) and its
// sold-out teas (data-deal-trio-sold-out, titles | joined); a tap adds the
// ones not in the basket yet and opens the basket (Michael 2026-10-12: "Basket
// should open"). The buttons hide
// once the basket holds all of them; the bar then names the trio as above.
//
// The deal is for Meta ad taps (?hd=<code>, deals 0.9.0): without a live
// pass the page shows an "offer ended" state instead (deal-unlock.js).
// ?tea= and ?trio= add only once the ad code is answered, and only for a
// live pass (or one that never came: Checkout then offers the deal-fail
// page's refund) - a shared link without a pass adds nothing - and only
// once the pass is on the cart, so the basket shows the deal price (review
// 2026-10-10).
import { addToCart } from '@split-infinity/cardo/cart'
import { openDrawer } from '@split-infinity/cardo/drawer'
import { unlockState, settled, passOnCart, passReady } from './deal-unlock.js'
import { shippingCents } from './shipping-choice.js'

const bar = () => document.querySelector('[data-deal-bar]')
const ordinal = (n) => ({ 1: '1st', 2: '2nd', 3: '3rd' })[n] || `${n}th`
const money = (cents) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100)

let cart = null
// Keeps a phrase on one line: a long line breaks only between phrases
// (Michael 2026-10-09), e.g. "Duchess of Bedford / is in your basket".
const whole = (phrase) => phrase.replaceAll(' ', '\u00a0')
// From a ?tea= link: the tea it named ({ id, title }) if in stock, or the
// title of a sold-out one.
let linked = null
let linkedSoldOut = null
// The trio from a ?trio= link: { name, line, ids, soldOut: [titles] }.
let trio = null

const soldOutTitle = (handle) =>
  (bar().dataset.dealSoldOut || '')
    .split('|')
    .map((entry) => entry.split('::'))
    .find(([h]) => h === handle)?.[1]

// A trio's tins by its key: the in-stock ones' small-tin ids (from their tea
// cards) and the sold-out ones' titles. A handle that is neither is skipped.
function trioOf(key) {
  const entry = (bar().dataset.dealTrios || '')
    .split('|')
    .map((e) => e.split('::'))
    .find(([k]) => k === key)
  if (!entry) return null
  const ids = []
  const soldOut = []
  for (const handle of entry[2].split(',')) {
    const button = document.querySelector(`[data-deal-add][data-deal-handle="${CSS.escape(handle)}"]`)
    if (button) ids.push(button.dataset.dealAdd)
    else if (soldOutTitle(handle)) soldOut.push(soldOutTitle(handle))
  }
  return { name: entry[1], line: entry[3], ids, soldOut }
}
// "A", "A and B", "A, B and C", each name kept whole.
const names = (list) => list.map(whole).join(', ').replace(/, ([^,]*)$/, ' and $1')

function render() {
  const el = bar()
  const tinIds = new Set((el.dataset.dealTinIds || '').trim().split('|'))
  const size = Number(el.dataset.dealSize)
  const inBasket = new Map()
  let tins = 0
  for (const line of cart.items) {
    const id = String(line.variant_id)
    inBasket.set(id, (inBasket.get(id) || 0) + line.quantity)
    if (tinIds.has(id)) tins += line.quantity
  }

  // Wording agreed with Michael 2026-10-04 - each line says exactly what
  // the next tap gets them. Tin prices only; Checkout shows the whole basket.
  const set = Number(el.dataset.dealSetPrice)
  const extra = Number(el.dataset.dealExtraPrice)
  const was = Number(el.dataset.dealWas)
  const tinsPrice = tins < size ? tins * was : set + (tins - size) * extra
  const saving = tins * was - tinsPrice
  let status
  let offer
  // ?tea= and trio wording approved by Michael 2026-10-10: a trio's name, then
  // the usual savings line.
  const others = trio && trio.soldOut.length
  if (others && tins < size) {
    status = `Sorry, ${names(trio.soldOut)} ${whole(others === 1 ? 'has sold out' : 'have sold out')}`
    offer = `${whole(others === 1 ? 'Pick another tin' : `Pick ${others} others`)} – ${whole(`all ${size} for ${money(set)}`)}`
  } else if (trio && tins === size && trio.ids.length === size && trio.ids.every((id) => inBasket.has(id))) {
    status = trio.line ? trio.line : `${whole(`Your ${trio.name}`)} ${whole('is in your basket')}`
    offer = `You're saving ${money(saving)} · add another for ${money(extra)}`
  } else if (tins === 0 && linkedSoldOut) {
    status = `Sorry, ${whole(linkedSoldOut)} ${whole('has sold out')}`
    offer = `${whole(`Pick any ${size} others`)} – ${whole(`all ${size} for ${money(set)}`)}`
  } else if (tins === 0) {
    status = `Any ${size} small tins for ${money(set)}`
    offer = `Usually ${money(size * was)} · delivered free`
  } else if (tins === 1 && linked && inBasket.get(linked.id) === 1) {
    status = `${whole(linked.title)} ${whole('is in your basket')}`
    offer = `${whole(`Add ${size - 1} more`)} – ${whole(`all ${size} for ${money(set)}`)}`
  } else if (tins < size - 1) {
    status = `${tins} of ${size} chosen`
    offer = `Add ${size - tins} more – all ${size} for ${money(set)}`
  } else if (tins === size - 1) {
    status = `${tins} of ${size} chosen`
    offer = `Add a ${ordinal(size)} tin for just ${money(set - tins * was)} more`
  } else if (tins === size) {
    status = `Deal unlocked – ${size} tins for ${money(set)}`
    offer = `You're saving ${money(saving)} · add another for ${money(extra)}`
  } else {
    status = `${tins} small tins for ${money(tinsPrice)}`
    offer = `You're saving ${money(saving)} · add another for ${money(extra)}`
  }
  // A trio page's add buttons go once its tins are all in.
  if (trio?.page) {
    const all = trio.ids.every((id) => inBasket.has(id))
    document.querySelectorAll('[data-deal-trio-add]').forEach((b) => (b.hidden = all))
  }
  el.querySelector('[data-deal-status]').textContent = status
  el.querySelector('[data-deal-bar-offer]').textContent = offer

  // One dot per tin towards the deal; past it, the dots stop and the count speaks.
  el.querySelector('[data-deal-slots]').replaceChildren(
    ...Array.from({ length: size }, (_, i) => {
      const dot = document.createElement('span')
      dot.className = `block h-xs w-xs rounded-full border-2 border-gold ${i < tins ? 'bg-gold' : ''}`
      return dot
    }),
  )
  // Checkout shows the whole basket's total - it checks out everything in it.
  // cart.total_price arrives with never-shipped items (gift cards) taken off
  // for the free-shipping goal (shipping-choice.js); add them back here
  // (2026-10-07 re-review R4). Shipping too, as the basket's Checkout button
  // shows it (2026-10-11 review): the rate goes by what is shipped.
  const checkout = el.querySelector('[data-deal-checkout]')
  checkout.hidden = tins < size
  checkout.textContent = `Checkout · ${money(cart.total_price + (cart.unshipped_off ?? 0) + shippingCents(cart))}`

  // A tea has one card, but more than one basket line can hold it.
  const keys = new Map()
  for (const line of cart.items) {
    const id = String(line.variant_id)
    keys.set(id, keys.has(id) ? `${keys.get(id)},${line.key}` : line.key)
  }
  document.querySelectorAll('[data-deal-stepper]').forEach((stepper) => {
    const id = stepper.dataset.dealStepper
    const n = inBasket.get(id) || 0
    const add = stepper.parentElement.querySelector('[data-deal-add]')
    // Same grid cell, so swapping them never changes the card's size.
    add.classList.toggle('invisible', n > 0)
    add.inert = n > 0
    stepper.classList.toggle('invisible', n === 0)
    stepper.inert = n === 0
    const input = stepper.querySelector('input')
    if (n > 0) input.setAttribute('data-cardo-cart-qty', keys.get(id))
    else input.removeAttribute('data-cardo-cart-qty')
    input.value = String(n)
    stepper.querySelector('[data-qty]')?.dispatchEvent(new CustomEvent('qty:sync', { bubbles: true }))
  })
}

// True once added. The button, if any, is disabled meanwhile.
async function add(ids, button) {
  if (button) button.disabled = true
  try {
    // A quick tap on arrival waits for the pass to be on the cart (deal-unlock.js).
    await passReady()
    await addToCart([].concat(ids).map((id) => ({ id: Number(id), quantity: 1 })), { context: 'deal' })
    return true
  } catch (error) {
    // Shopify's reason, e.g. that tea just sold out
    bar().querySelector('[data-deal-status]').textContent = error.message
    return false
  } finally {
    if (button) button.disabled = false
  }
}

// Retargeting arrival: once per visit, only if not already in the basket.
function addFromLink() {
  const tea = new URLSearchParams(location.search).get('tea')?.trim().toLowerCase()
  if (!tea) return
  const button = document.querySelector(`[data-deal-add][data-deal-handle="${CSS.escape(tea)}"]`)
  if (!button) {
    linkedSoldOut = soldOutTitle(tea)
    if (linkedSoldOut) render()
    return
  }
  const card = button.closest('[data-selection-card]')
  linked = { id: button.dataset.dealAdd, title: card.querySelector('h3').textContent.trim() }
  render()
  const key = `htwh:deal-link:${tea}`
  try {
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch {
    /* private mode: still add once for this page view */
  }
  const inBasket = cart.items.some((l) => String(l.variant_id) === button.dataset.dealAdd)
  if (!inBasket && !button.disabled) add(button.dataset.dealAdd, button)
}

// Trio ad arrival: once per visit, like ?tea=. Adds the tins not in the
// basket yet in one go, then opens the basket.
async function addTrioFromLink() {
  const key = new URLSearchParams(location.search).get('trio')?.trim().toLowerCase()
  trio = key && trioOf(key)
  if (!trio) return
  render()
  try {
    if (sessionStorage.getItem(`htwh:deal-trio:${key}`)) return
    sessionStorage.setItem(`htwh:deal-trio:${key}`, '1')
  } catch {
    /* private mode: still add once for this page view */
  }
  const missing = trio.ids.filter((id) => !cart.items.some((l) => String(l.variant_id) === id))
  if (missing.length && !(await add(missing))) return
  // With a tin sold out, the page's bar says so; the basket would hide it.
  if (trio.ids.length && !trio.soldOut.length) openDrawer('cart')
}

async function arrive() {
  const q = new URLSearchParams(location.search)
  if (!q.get('tea') && !q.get('trio')) return
  const state = await settled()
  if (state !== 'live' && state !== 'failed') return
  if (state === 'live') await passOnCart()
  addFromLink()
  addTrioFromLink()
}

// A trio page's trio, from its add button and the bar's one trio.
function trioOfPage() {
  const button = document.querySelector('[data-deal-trio-add]')
  const entry = (bar().dataset.dealTrios || '').split('::')
  if (!button || !entry[1]) return null
  const list = (v, sep) => (v || '').split(sep).filter(Boolean)
  return { page: true, name: entry[1], line: entry[3], ids: list(button.dataset.dealTrioIds, ','), soldOut: list(button.dataset.dealTrioSoldOut, '|') }
}

function start() {
  if (!bar()) return
  trio = trioOfPage()
  // The ad pass (deal-unlock.js): live or being checked shows the teas; an
  // ended pass, or none, shows that state instead (page-any-3-teas.liquid).
  const page = bar().closest('[data-deal-page]')
  document.addEventListener('deal:unlock', () => {
    if (page) page.dataset.dealState = unlockState()
  })
  let first = true
  document.addEventListener('cardo:cart', (e) => {
    cart = e.detail.cart
    render()
    if (!first) return
    first = false
    if (document.prerendering) {
      document.addEventListener('prerenderingchange', arrive, { once: true })
    } else {
      arrive()
    }
  })
  let trioAdding = false
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-deal-add]')
    if (button) add(button.dataset.dealAdd, button)
    const trioButton = e.target.closest('[data-deal-trio-add]')
    // One add at a time: the page's button and the bar's would otherwise both add the trio (review 2026-10-12).
    if (trioButton && cart && !trioAdding) {
      const missing = trio.ids.filter((id) => !cart.items.some((l) => String(l.variant_id) === id))
      if (!missing.length) openDrawer('cart')
      else {
        trioAdding = true
        add(missing, trioButton)
          .then((ok) => ok && openDrawer('cart'))
          .finally(() => (trioAdding = false))
      }
    }
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true })
} else {
  start()
}
